<template>
  <div class="map-wrapper">
    <gmap-map
      ref="map"
      :center="center"
      :options="options"
      class="map-wrapper__map"
      @dragStart="clearHover"
      @drag="clearHover"
      @click="handleMapClick"
    >
      <gmap-cluster
        :zoom-on-click="true"
        :ignore-hidden="true"
        :styles="clusterStyles"
      >
        <gmap-marker
          v-for="(marker, index) in markers"
          :key="index"
          :visible="marker.visible"
          :clickable="true"
          :icon="getIcon(marker.isVideo)"
          :position="marker.position"
          ref="googleMarker"
          @mouseover="handleMouseOver($event, marker)"
          @mouseout="handleMarkerMouseOut"
          @click="handleClick(marker.id)"
        />
      </gmap-cluster>
    </gmap-map>
    <div
      ref="details"
      class="map-wrapper__details"
      :class="{ 'map-wrapper__details--is-active': markerHover.active }"
      :style="{ left: numberToPx(markerHover.left), top: numberToPx(markerHover.top) }"
      @mouseleave="handleDetailsMouseLeave"
    >
      <details-component
        :name="markerHover.name"
        :photo="markerHover.photo"
        :address="markerHover.address"
        :is-video="markerHover.isVideo"
        @click="handleClick(markerHover.id)"
      />
    </div>
    <overlay-component type="map"/>
    <transition name="fade">
      <zoom-control
        v-if="controls"
        @up="handleZoomIn"
        @down="handleZoomOut"
      />
    </transition>
  </div>
</template>

<script>
import {
  MAP_CONFIG,
  MARKER_CONFIG,
  VIDEO_MARKER_CONFIG,
  scaleMarker
} from '@/assets/js/map'
import isInArea from '@/assets/js/isInArea'

import DetailsComponent from '@/containers/Map/Details'
import OverlayComponent from '@/components/Overlay'
import ZoomControl from '@/components/ZoomControl'

export default {
  components: {
    DetailsComponent,
    OverlayComponent,
    ZoomControl
  },
  props: {
    center: {
      type: Object,
      required: true
    },
    markers: {
      type: Array,
      default: () => []
    },
    controls: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      options: MAP_CONFIG,
      icon: MARKER_CONFIG,
      videoIcon: VIDEO_MARKER_CONFIG,
      markerHover: {},
      clusterStyles: Array(5).fill({
        height: 60,
        width: 60,
        url: '/img/cluster-icon.png',
        textColor: '#fff'
      }),
      mapObject: false
    }
  },
  computed: {
    numberToPx () {
      return number => number ? `${ number }px` : ''
    },
    getIcon () {
      return isVideo => isVideo ? this.videoIcon : this.icon
    }
  },
  watch: {
    markers (to, from) {
      this.$nextTick(() => {
        this.setMarkerIcon(from)
      })
    }
  },
  mounted () {
    this.setMarkerIcon()

    this.$nextTick(() => {
      this.$emit('mount')
    })

    this.$refs.map.$mapPromise.then(map => {
      this.mapObject = map
    })
  },
  methods: {
    handleClick (id) {
      this.clearHover()
      this.$emit('setActive', id)
    },
    handleMouseOver (evt, marker) {
      const { target } = this.findNativeEventField(evt)
      if (!target) return

      this.setHover(marker, target)
    },
    handleMarkerMouseOut (evt) {
      const nativeEvent = this.findNativeEventField(evt)
      // Checks if mouse is on the details box
      const cursorPosition = {
        x: nativeEvent.clientX,
        y: nativeEvent.clientY
      }
      const { width, height, left, top } = this.$refs.details.getBoundingClientRect()
      const detailsArea = { x1: left, x2: left + width, y1: top, y2: top + height }
      if (isInArea(detailsArea, cursorPosition)) {
        return
      }

      this.clearHover()
    },
    handleDetailsMouseLeave () {
      this.clearHover()
    },
    handleMapClick () {
      this.$emit('setActive', -1)
    },
    handleZoomIn () {
      if (!this.mapObject) return
      this.mapObject.setZoom(this.mapObject.getZoom() + 1)
    },
    handleZoomOut () {
      if (!this.mapObject) return
      this.mapObject.setZoom(this.mapObject.getZoom() - 1)
    },
    setHover (marker, googleMarker) {
      if (this.markerHover.active) return

      const { width, height, top, left } = googleMarker.getBoundingClientRect()
      this.markerHover = {
        ...marker,
        top: top - height / 2,
        left: left + width,
        active: true
      }
    },
    setMarkerIcon (from) {
      if (!this.$refs.googleMarker) return
      // Loop through markers from props
      this.markers.forEach((marker, index) => {
        this.$refs.googleMarker[index].$markerPromise.then(googleMarker => {
          if (marker.active) {
            scaleMarker(googleMarker, 1.4)
          } else if (from && from[index] && from[index].active) {
            scaleMarker(googleMarker, 1)
          }
        })
      })
    },
    clearHover () {
      this.markerHover.active = false
    },
    findNativeEventField (markerEvent) {
      // We need access to native event on marker mouseover and mouseout
      // Unfortunately google maps return it with random property name
      const entries = Object.entries(markerEvent)
      const [propertyName] = entries.find(entry => entry[1].target) || []
      return markerEvent[propertyName] || {}
    }
  }
}
</script>

<style lang="scss" scoped>
  .map-wrapper {
    overflow: hidden;

    position: fixed;
    height: 100%;
    width: 100%;
    z-index: -1;

    &__map {
      width: 100vw;
      height: 100%;
    }

    &__details {
      position: absolute;
      top: 0;
      left: 0;
      padding-left: 20px;

      pointer-events: none;
      opacity: 0;
      transform: translate3d(20px, -50%, 0);
      transition: opacity 0.2s ease-in-out,
                  transform 0.2s ease-in-out;

      &--is-active {
        opacity: 1;
        pointer-events: auto;
        transform: translate3d(0, -50%, 0);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/containers/Map/Map.vue